import React, {useEffect} from "react";
import PropTypes from "prop-types";
import {useFormik} from "formik";

import {ResponsiveStepperDialog} from "../../../components/ResponsiveStepperDialog";
import * as validator from "./validator";
import {bookingItemOption, formDataToAPIRequest} from "./utils";
import StepBooking from "./StepBooking";
import StepGuest from "./StepGuest";
import StepAmount from "./StepAmount";
import StepNotes from "./StepNotes";
import StepSummary from "./StepSummary";
import {useBookingCreate} from "../query";
import {useFeedbackTools} from "../../../components/FeedbackTools";
import StepBookingCondition from "./StepBookingCondition";
import useBookingConditionUtils from "./useBookingConditionUtils";
import {startOfDay} from "date-fns";

const steps = ['Prenotazione', 'Referente', 'Importo', 'Condizioni', 'Note', 'Verifica'];

const validationSchemas = [
    validator.schemaStepBooking,
    validator.schemaStepGuest,
    validator.schemaStepAmount,
    validator.schemaStepBookingCondition,
    null,
    validator.schemaBooking
];

const FormBooking = props => {
    const {booking, onClose, afterCreate, accommodations} = props;
    const bookingCreate = useBookingCreate();
    const {showSuccessNotification, showErrorNotification} = useFeedbackTools();
    const [activeStep, setActiveStep] = React.useState(0);
    const isLastStep = activeStep === steps.length - 1;

    const bookingConditionUtils = useBookingConditionUtils({booking});

    const formik = useFormik({
        initialValues: {
            bookingItems: booking.bookingItems.map((bi) => bookingItemOption(
                accommodations.findAccommodationUnit(bi.bookableType, bi.bookableId),
                bi.price ?? null)
            ),
            arrival: booking.getArrival(),
            departure: booking.getDeparture(),
            adults: booking.adults || '',
            children: booking.id ? (booking.children || 0) : 0,
            //childrenAge: [], never used - we use it when we integrate with livignoitaly request
            petsNumber: booking.id ? (booking.petsNumber || 0) : 0,
            email: booking.email || '',
            name: booking.name || '',
            surname: booking.surname || '',
            //language: '',  never used - we use it when we integrate with livignoitaly request
            nationality: booking.nationality ? booking.nationality.code : null,
            deposit: booking.bookingFinance.deposit ?? null,
            depositPayed: false,
            depositPayedAt: new Date(),
            depositPaymentType: "wire_transfer",
            hostNotes: booking.hostNotes || '',
            bookingCondition: bookingConditionUtils.initialBookingCondition,
            bookingConfirmationDocument: {
                sendEmailAfterCreate: true,
            },
        },
        validationSchema: validationSchemas[activeStep],
        onSubmit: async (values, actions) => {
            if (isLastStep) {
                try{
                    if(!booking.id){
                        // format date with iso string
                        const apiRequest = formDataToAPIRequest(values, true);
                        const newBooking = await bookingCreate.mutateAsync({data: apiRequest});
                        if (newBooking){
                            afterCreate(newBooking);
                        }
                    }else {
                        const apiRequest = formDataToAPIRequest(values, false);
                        await bookingCreate.mutateAsync({data: apiRequest});
                    }
                    onClose();
                    showSuccessNotification("Prenotazione creata con successo");
                } catch (e) {
                    showErrorNotification(e);
                }
            } else {
                setActiveStep(activeStep + 1);
                actions.setTouched({});
                actions.setSubmitting(false);
            }
        },
    });

    const handleBack = () => {
        setActiveStep(activeStep - 1);
    };

    const updateBookingConditionAfterArrivalChange = bookingConditionUtils.updateAfterArrivalChangeHandler(formik);
    const updateBookingConditionAfterBookingItemsChange = bookingConditionUtils.updateAfterBookingItemsChangeHandler(formik);

    const handleArrivalChange = (date) => {
        formik.setFieldValue("arrival", startOfDay(date));
        // Update booking conditions
        updateBookingConditionAfterArrivalChange(date)
    }

    const handleBookingItemsChange = (bookingItems) => {
        formik.setFieldValue("bookingItems", bookingItems);
        // Update booking conditions
        updateBookingConditionAfterBookingItemsChange(bookingItems);
    }

    const disableSendConfirmationEmail = formik.values.email === "" || formik.values.email === null || formik.values.email === undefined;

    useEffect( () => {
        if (disableSendConfirmationEmail) {
            formik.setFieldValue("bookingConfirmationDocument.sendEmailAfterCreate", false);
        } else {
            formik.setFieldValue("bookingConfirmationDocument.sendEmailAfterCreate", true);
        }
    }, [disableSendConfirmationEmail]);

    const getStepContent = () => {
        switch (activeStep) {
            case 0:
                return <StepBooking formik={formik} booking={booking} accommodations={accommodations}
                                    handleBookingItemsChange={handleBookingItemsChange}
                                    handleArrivalChange={handleArrivalChange}/>;
            case 1:
                return <StepGuest formik={formik}/>;
            case 2:
                return <StepAmount formik={formik} booking={booking} accommodations={accommodations}/>;
            case 3:
                return <StepBookingCondition formik={formik} bookingConditionUtils={bookingConditionUtils} />;
            case 4:
                return <StepNotes formik={formik}/>;
            case 5:
                return <StepSummary formik={formik}
                                    disableSendConfirmationEmail={disableSendConfirmationEmail}/>;
            default:
                throw new Error('Unknown step');
        }
    };

    const submitIsEnabled = !Object.keys(formik.errors).some(x => formik.touched[x]);

    return (
        <ResponsiveStepperDialog title={!!booking.id ? "Modifica prenotazione" : "Nuova prenotazione"}
                                 isOpen={true}
                                 isSaving={bookingCreate.isLoading}
                                 activeStep={activeStep}
                                 steps={steps}
                                 enableSubmit={submitIsEnabled}
                                 onClose={onClose}
                                 onBack={handleBack}
                                 onSubmit={formik.handleSubmit}
                                 disableBackdropClose
                                 noValidate>
            {getStepContent()}
        </ResponsiveStepperDialog>
    )
};

FormBooking.propTypes = {
    booking: PropTypes.object.isRequired,
    onClose: PropTypes.func.isRequired,
    afterCreate: PropTypes.func,
    accommodations: PropTypes.object.isRequired
};

export default FormBooking;